import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _PropTypes from 'prop-types';
import * as React from 'react';
import styled from 'styled-components';
import { KEYS } from '../../../constants/keys.js';
import { getTree } from '../../../utils/tree.js';
import { TreeNodeBase } from './TreeNodeBase.js';
import { jsx } from 'react/jsx-runtime';

var _excluded = ["filterBy", "focusIndex", "getAriaDropEffect", "getAriaGrabbed", "getAriaSelected", "hideBy", "nodeById", "onChangeHiddenIds", "onDefaultAction", "onExpand", "onFocusIndexChange", "orderBy", "renderNode"];
var StyledTreeBase = styled.div.withConfig({
  displayName: "TreeBase__StyledTreeBase",
  componentId: "sc-k1wie7-0"
})({
  "&:focus": {
    outline: "none"
  }
});

var TreeBase = function TreeBase(_ref) {
  var filterBy = _ref.filterBy,
      focusIndexProp = _ref.focusIndex,
      _ref$getAriaDropEffec = _ref.getAriaDropEffect,
      getAriaDropEffect = _ref$getAriaDropEffec === void 0 ? function () {
    return void 0;
  } : _ref$getAriaDropEffec,
      _ref$getAriaGrabbed = _ref.getAriaGrabbed,
      getAriaGrabbed = _ref$getAriaGrabbed === void 0 ? function () {
    return void 0;
  } : _ref$getAriaGrabbed,
      _ref$getAriaSelected = _ref.getAriaSelected,
      getAriaSelected = _ref$getAriaSelected === void 0 ? function () {
    return void 0;
  } : _ref$getAriaSelected,
      hideBy = _ref.hideBy,
      nodeById = _ref.nodeById,
      onChangeHiddenIds = _ref.onChangeHiddenIds,
      onDefaultAction = _ref.onDefaultAction,
      onExpand = _ref.onExpand,
      onFocusIndexChangeProp = _ref.onFocusIndexChange,
      orderBy = _ref.orderBy,
      renderNode = _ref.renderNode,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _React$useMemo = React.useMemo(function () {
    return getTree({
      filterBy: filterBy,
      hideBy: hideBy,
      nodeById: nodeById,
      orderBy: orderBy
    });
  }, [filterBy, hideBy, nodeById, orderBy]),
      context = _React$useMemo.context,
      nodeIds = _React$useMemo.nodeIds;

  var _React$useState = React.useState(focusIndexProp),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      focusIndex = _React$useState2[0],
      setFocusIndex = _React$useState2[1];

  React.useEffect(function () {
    setFocusIndex(focusIndexProp);
  }, [focusIndexProp]);

  var onFocusIndexChange = function onFocusIndexChange(val) {
    if (onFocusIndexChangeProp) {
      onFocusIndexChangeProp(val);
    }

    setFocusIndex(val);
  };

  var handleKeyDown = function handleKeyDown(event, id) {
    event.stopPropagation();
    var nodeContext = context.getNodeContext(id);

    switch (event.key) {
      case KEYS.ENTER:
      case KEYS.SPACE:
        event.preventDefault();
        onDefaultAction({
          context: nodeContext,
          event: event,
          node: context.nodeById[id]
        });
        return;

      case KEYS.ARROW_DOWN:
      case KEYS.PAGE_DOWN:
        event.preventDefault();
        return onFocusIndexChange(context.getNextVisibleAbsoluteIndex(nodeContext.absoluteIndex));

      case KEYS.END:
        event.preventDefault();
        onFocusIndexChange(context.getLastVisibleIndex());
        return;

      case KEYS.HOME:
        event.preventDefault();
        onFocusIndexChange(0);
        return;

      case KEYS.ARROW_LEFT:
        event.preventDefault();

        if (nodeContext.isExpanded()) {
          var nextHiddenIds = new Set(context.hiddenIds);
          nodeContext.getChildIds().forEach(function (childId) {
            nextHiddenIds.add(childId);
          });
          return onChangeHiddenIds(nextHiddenIds);
        } else {
          return onFocusIndexChange(context.getPrevVisibleAbsoluteIndex(nodeContext.absoluteIndex));
        }

      case KEYS.ARROW_RIGHT:
        event.preventDefault();

        if (nodeContext.isCollapsed()) {
          var _nextHiddenIds = new Set(context.hiddenIds);

          nodeContext.getChildIds().forEach(function (childId) {
            _nextHiddenIds.delete(childId);
          });

          if (typeof onExpand === "function") {
            onExpand({
              context: nodeContext,
              node: nodeById[nodeContext.id]
            });
          }

          return onChangeHiddenIds(_nextHiddenIds);
        } else if (nodeContext.isExpanded()) {
          return onFocusIndexChange(context.getNextVisibleAbsoluteIndex(nodeContext.absoluteIndex));
        } else {
          return;
        }

      case KEYS.ARROW_UP:
      case KEYS.PAGE_UP:
        event.preventDefault();
        return onFocusIndexChange(context.getPrevVisibleAbsoluteIndex(nodeContext.absoluteIndex));
    }
  };

  return /*#__PURE__*/jsx(StyledTreeBase, {
    role: "treegrid",
    "aria-multiselectable": rest["aria-multiselectable"],
    "aria-rowcount": context.getNodeCount(),
    "data-testid": "mntl-tree-base",
    onFocus: function onFocus() {
      return onFocusIndexChange(0);
    },
    tabIndex: 0,
    children: nodeIds.map(function (id) {
      var node = nodeById[id];
      var nodeContext = context.getNodeContext(id);
      var focused = focusIndex === context.getNodeContext(id).absoluteIndex;
      var renderProps = {
        context: nodeContext,
        focused: focused,
        node: node
      };
      return /*#__PURE__*/jsx(TreeNodeBase, {
        "aria-dropeffect": getAriaDropEffect(renderProps),
        "aria-expanded": nodeContext.getAriaExpanded(),
        "aria-grabbed": getAriaGrabbed(renderProps),
        "aria-level": nodeContext.getAriaLevel(),
        "aria-posinset": nodeContext.getAriaPosInSet(),
        "aria-selected": getAriaSelected(renderProps),
        "aria-setsize": nodeContext.getAriaSetSize(),
        "aria-rowindex": nodeContext.getAriaRowIndex(),
        "data-testid": "mntl-tree-node-base",
        focused: focused,
        onBlur: function onBlur(event) {
          if (event.target.getAttribute("role") === "row") {
            onFocusIndexChange(void 0);
          }
        },
        onFocus: function onFocus(event) {
          event.stopPropagation();
          onFocusIndexChange(nodeContext.absoluteIndex);
        },
        onKeyDown: function onKeyDown(event) {
          return handleKeyDown(event, id);
        },
        role: "row",
        tabIndex: focused ? 0 : -1,
        children: renderNode(renderProps)
      }, nodeContext.id);
    })
  });
};

TreeBase.propTypes = {
  "filterBy": _PropTypes.func,
  "focusIndex": _PropTypes.number,
  "getAriaDropEffect": _PropTypes.func,
  "getAriaGrabbed": _PropTypes.func,
  "getAriaSelected": _PropTypes.func,
  "hideBy": _PropTypes.func,
  "nodeById": _PropTypes.object.isRequired,
  "onChangeHiddenIds": _PropTypes.func.isRequired,
  "onDefaultAction": _PropTypes.func.isRequired,
  "onExpand": _PropTypes.func,
  "onFocusIndexChange": _PropTypes.func,
  "orderBy": _PropTypes.func,
  "renderNode": _PropTypes.func.isRequired
};
TreeBase.displayName = "TreeBase";

export { StyledTreeBase, TreeBase };
